/**
 * MyScheduleAView — shows THIS user's Schedule A from their direct upline.
 *
 * CRITICAL: Downlines (agent/subagent) NEVER see "effective entitlement %"
 * — they only see: Direct Upline Gross → Assigned Split → Net Residual.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import useAuthStore from '../../lib/auth-store';
import api from '../../lib/api';

const money = (n) => (typeof n === 'number' ? `$${n.toFixed(2)}` : '$0.00');
const pct = (n) => (typeof n === 'number' ? `${n.toFixed(2)}%` : '0.00%');

export default function MyScheduleAView() {
  const { user } = useAuthStore();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const d = await api.getScheduleAMyView();
        setData(d);
      } catch (e) {
        toast.error('Failed to load your Schedule A');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-sm text-muted-foreground" data-testid="my-schedule-loading">Loading…</div>;
  if (!data?.has_schedule_a) {
    return (
      <Card data-testid="my-schedule-empty">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            No Schedule A assigned yet. Contact your direct upline to set up your compensation.
          </p>
        </CardContent>
      </Card>
    );
  }

  const uplineLabel = user?.role === 'iso' ? 'MSC Gross Residual' :
    user?.role === 'iso_agent' ? 'ISO Gross Residual' :
    user?.role === 'iso_subagent' ? 'Agency Gross Residual' : 'Direct Upline Gross Residual';

  return (
    <div className="space-y-6" data-testid="my-schedule-view">
      {/* Compensation Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Current Compensation
            <Badge variant="secondary" data-testid="my-schedule-comp-type">
              {data.compensation_type === 'tiered' ? 'Tiered' : 'Fixed'}
            </Badge>
            <Badge variant="outline">v{data.version}</Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Effective {data.effective_date} • From {data.upline_name} ({data.upline_role?.replace('_', ' ')})
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatBlock label={uplineLabel} value={money(data.direct_upline_gross_residual)} dataTestId="upline-gross" />
            <StatBlock
              label="Your Split"
              value={`${pct(data.current_split_percent)} ${data.compensation_type === 'tiered' ? 'Tiered' : 'Fixed'}`}
              dataTestId="my-split"
            />
            <StatBlock label="Your Net Residual" value={money(data.my_net_residual)} highlight dataTestId="my-net" />
          </div>
          {data.direct_upline_gross_residual === 0 && (
            <p className="text-xs text-muted-foreground mt-4 italic" data-testid="my-schedule-empty-residuals">
              No residuals calculated for {data.period} yet. Once processor sync + recalculation run for this period,
              your Direct Upline Gross Residual and Net will appear here.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Tier progress (only if tiered) */}
      {data.compensation_type === 'tiered' && data.tier_info && (
        <Card data-testid="my-schedule-tier-progress">
          <CardHeader>
            <CardTitle>Tier Progress</CardTitle>
            <p className="text-xs text-muted-foreground">Volume window: {data.tier_volume_method}</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatBlock label="Qualifying Volume" value={money(data.qualifying_volume)} />
              <StatBlock label="Current Tier" value={pct(data.tier_info.current_split_percent || 0)} />
              {data.tier_info.next_split_percent && (
                <>
                  <StatBlock label="Next Tier" value={pct(data.tier_info.next_split_percent)} />
                  <StatBlock label="Volume to Next Tier" value={money(data.tier_info.volume_to_next_tier || 0)} />
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ISO ONLY: Cost Schedule */}
      {user?.role === 'iso' && data.cost_schedule && (
        <Card data-testid="my-schedule-cost">
          <CardHeader>
            <CardTitle>Cost Schedule ({data.cost_schedule.processor_slug?.toUpperCase()})</CardTitle>
            <p className="text-xs text-muted-foreground">
              Template: {data.cost_schedule.template_name} • Effective {data.cost_schedule.effective_date}
            </p>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2">Fee</th>
                  <th>Unit</th>
                  <th className="text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {(data.cost_schedule.cost_lines || []).map((line) => (
                  <tr key={line.fee_code} className="border-b last:border-0">
                    <td className="py-2">{line.label || line.fee_code}</td>
                    <td className="text-muted-foreground">{line.unit}</td>
                    <td className="text-right font-mono">
                      {line.unit === 'bps' ? `${line.amount} bps` : `$${line.amount.toFixed(4)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatBlock({ label, value, highlight, dataTestId }) {
  return (
    <div
      className={`rounded-lg border p-4 ${highlight ? 'bg-primary/5 border-primary/30' : ''}`}
      data-testid={dataTestId}
    >
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${highlight ? 'text-primary' : ''}`}>{value}</div>
    </div>
  );
}
